/**
 * Document Upload Utilities
 *
 * Uploads files directly to S3 via presigned PUT URLs obtained from the backend.
 * No external imagex service dependency.
 *
 * Flow:
 *   1. Frontend calls backend POST /api/v1/documents/presigned-upload-url
 *      to get a short-lived presigned PUT URL + bucket/key metadata.
 *   2. Frontend PUTs the raw file body to the presigned URL (direct to S3).
 *   3. Returns UploadResponseData so callers don't need to change.
 */

import { documentsApi } from '../api/endpoints/documents.api';
import { PUBLIC_CDN_BASE_URL } from './publicCdn.utils';
import type {
  UploadResponseData,
  UploadOptions,
  MultipleUploadResult,
} from '@/types/upload.types';

/**
 * Upload a single file to S3 via presigned PUT URL.
 *
 * @example
 * const result = await uploadDocument(file, {
 *   folderPath: 'agencies/documents',
 * });
 *
 * @example
 * const result = await uploadDocument(file, {
 *   folderPath: 'agencies/logos',
 *   bucketType: 'public',
 * });
 */
export const uploadDocument = async (
  file: File,
  options: UploadOptions
): Promise<UploadResponseData> => {
  if (!file) {
    throw new Error('No file provided for upload');
  }

  const fileName = options.fileName || file.name;
  const contentType = file.type || 'application/octet-stream';
  const bucketType = options.bucketType || 'private';

  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.debug('[uploadDocument] start', {
      folderPath: options.folderPath,
      fileName,
      contentType,
      bucketType,
      fileSize: file.size,
    });
  }

  // Step 1: get presigned PUT URL from our backend
  const presigned = await documentsApi.getPresignedUploadUrl(
    options.folderPath,
    fileName,
    contentType,
    bucketType
  );

  // Step 2: PUT file directly to S3
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.debug('[uploadDocument] got presigned', {
      bucket: presigned.bucket,
      key: presigned.key,
      hasCdnUrl: !!presigned.cdnUrl,
      uploadUrlHost: (() => {
        try {
          return new URL(presigned.uploadUrl).host;
        } catch {
          return 'invalid-url';
        }
      })(),
    });
  }
  const putResponse = await fetch(presigned.uploadUrl, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': contentType,
    },
    mode: 'cors',
  });

  if (!putResponse.ok) {
    throw new Error(`S3 upload failed with status ${putResponse.status}`);
  }

  // Step 3: build UploadResponseData for the caller
  const s3Url = `s3://${presigned.bucket}/${presigned.key}`;
  const baseUrl = `https://${presigned.bucket}.s3.amazonaws.com`;
  // Public bucket objects are only readable via CloudFront (S3 direct returns 403)
  const cdnUrl =
    bucketType === 'public'
      ? (presigned.cdnUrl || `${PUBLIC_CDN_BASE_URL}/${presigned.key}`)
      : (presigned.cdnUrl || `${baseUrl}/${presigned.key}`);

  return {
    fileName,
    bucket: presigned.bucket,
    key: presigned.key,
    baseUrl,
    cdnUrl,
    contentType,
    fileSize: file.size,
    s3Url,
  };
};

/**
 * Upload multiple files sequentially
 */
export const uploadDocuments = async (
  files: File[],
  options: UploadOptions
): Promise<MultipleUploadResult> => {
  const result: MultipleUploadResult = { success: [], failed: [] };

  for (const file of files) {
    try {
      const uploadResult = await uploadDocument(file, options);
      result.success.push(uploadResult);
    } catch (error: any) {
      result.failed.push({ file, error: error.message || 'Upload failed' });
    }
  }

  return result;
};

/**
 * Upload multiple files in parallel
 */
export const uploadDocumentsParallel = async (
  files: File[],
  options: UploadOptions
): Promise<MultipleUploadResult> => {
  const results = await Promise.all(
    files.map(async (file) => {
      try {
        const data = await uploadDocument(file, options);
        return { ok: true as const, data, file: null as unknown as File };
      } catch (error: any) {
        return { ok: false as const, data: null, file, error: error.message || 'Upload failed' };
      }
    })
  );

  const uploadResult: MultipleUploadResult = { success: [], failed: [] };
  for (const r of results) {
    if (r.ok && r.data) {
      uploadResult.success.push(r.data);
    } else if (!r.ok && r.file) {
      uploadResult.failed.push({ file: r.file, error: r.error });
    }
  }
  return uploadResult;
};

/**
 * Validate file before upload
 */
export const validateFile = (
  file: File,
  maxSizeInMB: number = 10,
  allowedTypes?: string[]
): { isValid: boolean; error?: string } => {
  const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
  if (file.size > maxSizeInBytes) {
    return { isValid: false, error: `File size exceeds ${maxSizeInMB}MB limit` };
  }
  if (allowedTypes && allowedTypes.length > 0 && !allowedTypes.includes(file.type)) {
    return { isValid: false, error: `File type not allowed. Allowed types: ${allowedTypes.join(', ')}` };
  }
  return { isValid: true };
};

/**
 * Get file extension from filename
 */
export const getFileExtension = (fileName: string): string => {
  const parts = fileName.split('.');
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
};

/**
 * Format file size for display
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

const documentUploadUtils = {
  uploadDocument,
  uploadDocuments,
  uploadDocumentsParallel,
  validateFile,
  getFileExtension,
  formatFileSize,
};

export default documentUploadUtils;
